import React, { useState } from "react";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PageHeader from "../../shared/components/PageHeader";
import useNavMenu from "../../shared/useNavMenu";

// Quick Find field for the Staff header. The in-progress text is local state (a
// form draft, kept off the URL per the v2 view-state convention); it is applied
// to the page only on submit or clear, which is when v1 writes the `query` param
// and the body refetches. StaffHeader keys this on the applied query, so an
// external reset (switching tabs clears the search) remounts it with the new
// seed rather than needing a sync effect.
function StaffSearchField({ query, onSearch }) {
  const [value, setValue] = useState(query ?? "");

  const submit = (e) => {
    e.preventDefault();
    onSearch(value.trim());
  };

  const clear = () => {
    setValue("");
    onSearch("");
  };

  return (
    <form
      onSubmit={submit}
      style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
    >
      <TextField
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Quick Find"
        variant="standard"
        size="small"
        sx={{ width: { xs: "100%", md: 240 } }}
        slotProps={{
          input: {
            "aria-label": "Quick Find",
            endAdornment: (
              <InputAdornment position="end">
                {value ? (
                  <IconButton
                    aria-label="Clear search"
                    onClick={clear}
                    edge="end"
                  >
                    <ClearRoundedIcon />
                  </IconButton>
                ) : (
                  <IconButton aria-label="Search" type="submit" edge="end">
                    <SearchRoundedIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          },
        }}
      />
    </form>
  );
}

// Page-header definition for the legacy v1 Staff pages (Staff and Temporary
// Staff). The tab strip resolves from the navigation registry (useNavMenu);
// v1 supplies only the Quick Find query + its handlers. expandableActions lets
// the field grow to fill the row on mobile while it holds focus. Tab navigation
// flows through the bridge's navigate (params.onNavigate) which clears the search.
export default function StaffHeader({ query, onSearch }) {
  const { tabs, activeKey } = useNavMenu("staff");

  return (
    <PageHeader
      title="Staff"
      expandableActions
      tabs={tabs}
      activeTab={activeKey}
      actions={
        <StaffSearchField key={query} query={query} onSearch={onSearch} />
      }
    />
  );
}
